import { useMemo } from "react";

import { IconPlus, IconRefresh } from "@tabler/icons";
import { useTheme } from "styled-components";

import { useProductsQuery } from "@sso/apis/products";
import { useGetUsersQuery } from "@sso/apis/users/hooks";
import { TableHeader } from "@sso/components/TableHeader";
import { TableLayoutWrapper } from "@sso/components/TableLayoutWrapper";
import { TablePagination } from "@sso/components/TablePagination";
import { Dialog, UserKeys, UserRole } from "@sso/constant";
import { TableProvider, TableProviderState } from "@sso/context/tableContext";
import { useAppDispatch, useAppSelector } from "@sso/hooks/useRedux";
import { selectCurrentUser } from "@sso/store/slices/auth";
import { openDialog } from "@sso/store/slices/dialog";
import { selectUserPagination } from "@sso/store/slices/user";
import { SearchFieldKeys, SearchParameterKeys } from "@sso/types";
import { CircleLoading } from "@sso/ui/CircleLoading";
import { Flex } from "@sso/ui/FlexBox";
import { IconButton } from "@sso/ui/IconButton";

import { UserTable } from "./UserTable";
import { userTableColumns } from "./userTableColumns";

export const Users = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const user = useAppSelector(selectCurrentUser);
  const userPagination = useAppSelector(selectUserPagination);
  const { data, isFetching, error, refetch } = useGetUsersQuery();
  const { data: allProducts } = useProductsQuery();

  const userModifiedTableColumns = useMemo(() => {
    let columns = [...userTableColumns];
    if (user?.role.roleId === UserRole.ORG_ADMIN) {
      columns = columns.filter(item => item.field !== UserKeys.ORGANISATION_NAME);
    }
    if (!allProducts || allProducts.length <= 0) {
      return columns;
    } else {
      const productsListOption = allProducts.map(product => ({
        key: product.softwareId,
        value: product.name,
        label: product.name,
      }));
      return columns.map(col => {
        if (col.field === UserKeys.SOFTWARES_NAME) {
          return {
            ...col,
            search: {
              type: SearchFieldKeys.SELECT,
              options: productsListOption,
              searchParamaterKey: SearchParameterKeys.CONTAIN,
              field: "Softwares.Name",
            },
          };
        }
        return col;
      });
    }
  }, [allProducts, user?.role.roleId]);

  const tableContextInstance: TableProviderState = { ...userPagination, columns: userModifiedTableColumns };

  const handleOpenUserDialog = () => {
    dispatch(openDialog({ type: Dialog.ADD_USER, data: user?.organisation.organisationId }));
  };

  const TableActions = (
    <Flex>
      <IconButton title="Refresh" onClick={refetch}>
        <IconRefresh />
      </IconButton>
      {user?.role.roleId === UserRole.ORG_ADMIN && (
        <IconButton title="Add Users" onClick={handleOpenUserDialog}>
          <IconPlus />
        </IconButton>
      )}
    </Flex>
  );

  return (
    <>
      {isFetching && <CircleLoading color={theme.colors.secondary.dark} size={8} blurBackground />}
      <TableProvider tableInstance={tableContextInstance}>
        <TableHeader title="Users" TableActions={TableActions} />
        <TableLayoutWrapper error={error} data={data}>
          {data && <UserTable userList={data.resources} columns={userModifiedTableColumns} />}
        </TableLayoutWrapper>
        <TablePagination totalPages={data?.totalPages} />
      </TableProvider>
    </>
  );
};
